import { useState, useEffect, useCallback } from "react";
import { doc, onSnapshot, updateDoc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

// Hook central de "cuentas compartidas": sabe en qué workspace está
// trabajando el usuario ahora, permite cambiar entre su workspace personal
// y los compartidos a los que pertenece, e invitar/unirse a otros.
export function useWorkspace() {
  const { user, profile } = useAuth();
  const [workspaces, setWorkspaces] = useState([]); // detalles de cada workspace al que pertenece
  const [loading, setLoading] = useState(true);

  const activeWorkspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const workspaceIds = profile?.workspaceIds || (profile?.personalWorkspaceId ? [profile.personalWorkspaceId] : []);

  // Suscripción en vivo a los documentos de cada workspace al que pertenece
  // el usuario, para reflejar cambios de nombre o de miembros en tiempo real.
  useEffect(() => {
    if (!user || workspaceIds.length === 0) { setWorkspaces([]); setLoading(false); return; }

    // En cuanto cambia la lista de workspaceIds (saliste de uno, te
    // expulsaron, lo borraron...), quitamos del estado local cualquier
    // workspace que ya no esté en la lista actual. Sin esto, un workspace
    // del que ya no formas parte se quedaba "fantasma" en memoria hasta
    // recargar la página entera.
    setWorkspaces((prev) => prev.filter((w) => workspaceIds.includes(w.id)));

    const unsubs = workspaceIds.map((wsId) =>
      onSnapshot(doc(db, "workspaces", wsId), (snap) => {
        if (!snap.exists()) {
          // El workspace se borró (p.ej. alguien dejó de compartirlo):
          // lo quitamos también del estado local al instante.
          setWorkspaces((prev) => prev.filter((w) => w.id !== wsId));
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setWorkspaces((prev) => {
          const others = prev.filter((w) => w.id !== data.id);
          return [...others, data];
        });
        setLoading(false);
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [user, JSON.stringify(workspaceIds)]);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;
  const isPersonalActive = activeWorkspaceId === profile?.personalWorkspaceId;

  // Cambia el workspace activo (afecta a qué entries/pendientes/etc. ve la app)
  async function switchWorkspace(workspaceId) {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { activeWorkspaceId: workspaceId });
  }

  // Genera un código de invitación de 6 caracteres válido 7 días, para
  // compartir el workspace activo con otra persona. mode solo importa si el
  // workspace activo es el personal: "empty" (nuevo espacio vacío, por
  // defecto), "copy" (lleva una copia de los datos actuales) o "move"
  // (mueve los datos actuales, quitándolos del personal).
  const createInvite = useCallback(async (workspaceId, mode = "empty") => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "createWorkspaceInvite");
    const result = await fn({ workspaceId: workspaceId || activeWorkspaceId, mode });
    return result.data; // { code, expiresAt, workspaceId }
  }, [activeWorkspaceId]);

  // Usa un código de invitación para unirse a un workspace compartido
  const joinWithCode = useCallback(async (code) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "joinWorkspace");
    const result = await fn({ code });
    return result.data; // { success, workspaceId, workspaceName }
  }, []);

  // Sale de un workspace compartido (no del personal)
  const leaveWorkspace = useCallback(async (workspaceId) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "leaveWorkspace");
    const result = await fn({ workspaceId });
    return result.data;
  }, []);

  // Solo el propietario: deja de compartir el workspace, expulsando a todos
  // los demás miembros y borrando el espacio compartido por completo (ya no
  // queda como un workspace vacío en la lista de nadie). keepData=true
  // fusiona los datos actuales con el workspace personal antes de borrar.
  const unshareWorkspace = useCallback(async (workspaceId, keepData = false) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "unshareWorkspace");
    const result = await fn({ workspaceId, keepData });
    return result.data; // { success, removedCount }
  }, []);

  // Solo el propietario: expulsa a un único miembro concreto, sin afectar
  // al resto. Útil cuando hay 3+ miembros y solo quieres quitar a uno.
  const removeMember = useCallback(async (workspaceId, memberId) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "removeMember");
    const result = await fn({ workspaceId, memberId });
    return result.data;
  }, []);

  // Traspasa entries/pendientes/recurrents/recurringFlags desde el workspace
  // personal hacia un workspace compartido del que el usuario es propietario.
  // mode: "copy" (se quedan también en personal) o "move" (se eliminan del personal).
  const transferDataToWorkspace = useCallback(async (targetWorkspaceId, mode) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "transferDataToWorkspace");
    const result = await fn({ targetWorkspaceId, mode });
    return result.data; // { success, mode, counts }
  }, []);

  // Devuelve el nombre/email de cada miembro de un workspace (para mostrar
  // "compartido con María"). Hace lecturas puntuales, no en vivo.
  // Devuelve { uid, name } de cada miembro de un workspace, leyendo del
  // directorio público mínimo (memberDirectory), visible para cualquier
  // autenticado, en vez de users/{uid} que está restringido al propio dueño.
  async function getMemberNames(workspaceId) {
    const ws = workspaces.find((w) => w.id === workspaceId);
    if (!ws) return [];
    const members = await Promise.all(
      (ws.members || []).map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "memberDirectory", uid));
          const label = snap.exists() ? (snap.data().name || snap.data().email || uid) : uid;
          return { uid, name: label };
        } catch (e) {
          return { uid, name: uid };
        }
      })
    );
    return members;
  }

  return {
    workspaces, loading,
    activeWorkspaceId, activeWorkspace, isPersonalActive,
    switchWorkspace, createInvite, joinWithCode, leaveWorkspace, unshareWorkspace, removeMember, transferDataToWorkspace, getMemberNames,
  };
}
